import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import useGetMyTaxRates from '@/api/queries/me/useGetMyTaxRates';
import { MaterialAndSuppliers } from '@/api/queries/project/useGetProjectMaterialListSummaryItems';
import useCreateMaterialPurchaseRecord from '@/api/rest/materials/useCreateMaterialPurchaseRecord';
import { registerFieldLegacyBuilder } from '@/utils/zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CurrencyInput,
  Field,
  Modal,
  RawInput,
  Select,
} from '@payaca/ambrosia';

import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { MaterialPurchaseRecordSupplierSelectionControl } from './MaterialPurchaseRecordSupplierSelectionControl';

const purchaseSchema = z.object({
  supplierId: z.string().min(1),
  unitPrice: z.number().int().positive(),
  taxRateId: z.string().nullable(),
  quantity: z.number().gt(0),
});
type PurchaseSchema = z.infer<typeof purchaseSchema>;

export type PurchaseFormDefaultValues = Partial<PurchaseSchema>;

export const RecordPurchaseModal: FC<{
  materialsListId: number;
  materialAndSuppliers: MaterialAndSuppliers;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({
  materialsListId,
  materialAndSuppliers,
  isOpen,
  onSuccess,
  onClose,
}) => {
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const {
    taxRates,
    defaultTaxRate,
    isLoading: isLoadingTaxRates,
  } = useGetMyTaxRates();

  const firstSupplier = materialAndSuppliers.material.suppliedBy.find(
    (supplierMaterial) => supplierMaterial.supplier
  );
  const defaultValues: PurchaseFormDefaultValues = {
    supplierId: firstSupplier?.supplier.id,
    unitPrice: firstSupplier?.price.unitPriceExcTax.value,
    taxRateId: null,
    quantity:
      materialAndSuppliers.quantity - materialAndSuppliers.quantityOrdered,
  };
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PurchaseSchema>({
    defaultValues,
    resolver: zodResolver(purchaseSchema),
  });
  const registerField = registerFieldLegacyBuilder(errors);

  const { mutateAsync, isPending } = useCreateMaterialPurchaseRecord();

  const onSubmit = async (data: PurchaseSchema) => {
    // Fall back to the default tax rate the Select shows when none is picked.
    const taxRateId = data.taxRateId || defaultTaxRate?.id;
    await mutateAsync({
      materialsListMaterialId: +materialAndSuppliers.id,
      materialsListId: materialsListId,
      supplierId: +data.supplierId,
      price: data.unitPrice,
      taxRateId: taxRateId ? +taxRateId : undefined,
      materialQuantity: data.quantity,
    }).then(() => {
      onSuccess();
    });
  };
  return (
    <Modal title="Record purchase" isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Field.Legacy {...registerField('supplierId')}>
            <Field.Label>Supplier</Field.Label>
            <Controller
              name="supplierId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <MaterialPurchaseRecordSupplierSelectionControl
                  materialAndSuppliers={materialAndSuppliers}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <span className="text-sm text-gray-500">
              Choose a known supplier, or type to search across all existing
              suppliers
            </span>
          </Field.Legacy>
          <div className="flex gap-4">
            <Field.Legacy className="flex-1" {...registerField('unitPrice')}>
              <Field.Label>Unit price</Field.Label>
              <Controller
                name="unitPrice"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CurrencyInput
                    value={value}
                    onChange={onChange}
                    currency={regionPreferences?.currency}
                  />
                )}
              />
            </Field.Legacy>
            <Field.Legacy className="flex-1" {...registerField('taxRateId')}>
              <Field.Label>Tax</Field.Label>
              <Controller
                name="taxRateId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={
                      taxRates
                        ?.filter((taxRate) => !taxRate.isReverseCharge)
                        .map((taxRate) => ({
                          label: taxRate.label,
                          value: taxRate.id,
                        })) || []
                    }
                    value={value || defaultTaxRate?.id}
                    onChange={onChange}
                    disabled={isLoadingTaxRates}
                  />
                )}
              />
            </Field.Legacy>
            <Field.Legacy className="flex-1" {...registerField('quantity')}>
              <Field.Label>Quantity</Field.Label>
              <RawInput {...register('quantity', { valueAsNumber: true })} />
            </Field.Legacy>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" isProcessing={isPending}>
            Record purchase
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
